import { createContext, useContext, useMemo } from 'react';
import type { CSSProperties, PropsWithChildren } from 'react';
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, ListItem } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface Props {
  id: UniqueIdentifier;
  style?: CSSProperties;
  disabled?: boolean;
}

interface Context {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableItem({
  children,
  id,
  style: itemStyle,
  disabled,
}: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    boxSizing: 'border-box',
    ...itemStyle,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <ListItem className="SortableItem" ref={setNodeRef} style={style}>
        {children}
      </ListItem>
    </SortableItemContext.Provider>
  );
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <Button
      type="button"
      onClick={event => {
        event.stopPropagation();
      }}
      className="DragHandle"
      variant="link"
      sx={theme => ({
        color: theme.palette.outlineGrey.main,
        minWidth: 0,
        paddingX: 0.5,
        cursor: 'move',
        touchAction: 'none',
      })}
      {...attributes}
      {...listeners}
      ref={ref}>
      <DragIndicatorIcon fontSize="small" />
    </Button>
  );
}
